import * as React from "react";
import { useFormContext } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../components/ui/accordion";
import { ScrollArea } from "../../../components/ui/scroll-area";
import { CheckCircle2, XCircle, AlertTriangle, Info, CalendarDays, Truck, User, FileSpreadsheet, ShieldCheck, PackageSearch, Car, Layers } from "lucide-react";
import { motion } from "framer-motion";
import { getPeriodLabels } from "../utils/periodLabels";
import { getRequiredDocumentTypes } from "../utils/schema";

/* ----------------------- helpers ----------------------- */
const isEmpty = (v) =>
  v == null ||
  (typeof v === "string" && v.trim() === "") ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt?.getTime?.())) return String(d);
  return dt.toLocaleDateString();
};

const YesNo = ({ v }) => {
  const normalized = typeof v === "string" ? v : v === true ? "yes" : v === false ? "no" : "na";
  if (normalized === "yes") return <Badge className="bg-emerald-500 text-white hover:bg-emerald-500">Yes</Badge>;
  if (normalized === "no") return <Badge className="bg-rose-500 text-white hover:bg-rose-500">No</Badge>;
  return <Badge variant="secondary">N/A</Badge>;
};

const Stat = ({ icon: Icon, label, value, hint }) => (
  <div className="flex items-start gap-3">
    <div className="mt-1"><Icon className="w-4 h-4 text-muted-foreground" /></div>
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-base font-medium text-slate-900 truncate">{value}</div>
      {hint ? <div className="text-[11px] text-muted-foreground">{hint}</div> : null}
    </div>
  </div>
);

const Pill = ({ children }) => (
  <span className="inline-flex items-center rounded-full border bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">
    {children}
  </span>
);

/* ------------------- compact key/value grid ------------------- */
const Field = ({ k, v }) => (
  <div className="space-y-1">
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{k}</div>
    <div className="text-sm text-slate-900 border rounded-md bg-white px-3 py-2 break-words">
      {typeof v === "boolean" ? <YesNo v={v} /> : String(v ?? "—")}
    </div>
  </div>
);

function flatten(obj, prefix = "") {
  return Object.entries(obj).flatMap(([k, v]) => {
    const newKey = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      return flatten(v, newKey);
    }
    return [[newKey, v]];
  });
}



const KVFlat = ({ data }) => {
  if (!data || typeof data !== "object") return null;
  const entries = flatten(data).filter(([k, _]) => k.toLowerCase() !== 'id' && k.toLowerCase() !== 'driverid' && k.toLowerCase() !== 'vehicleid' && k.toLowerCase() !== 'trailerid' && k.toLowerCase() !== 'quoteid' && k.toLowerCase() !== 'stepid' && k.toLowerCase() !== 'createdat' && k.toLowerCase() !== 'updatedat');
  if (entries.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {entries.map(([k, v]) => (
        <Field key={k} k={labelize(k)} v={v} />
      ))}
    </div>
  );
};



const labelize = (s) => String(s).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

/* ------------------- section highlights ------------------- */
const LABELS = {
  carrier: "Carrier",
  policyDate: "Policy",
  hazmatPlacard: "Hazmat",
  haulDoubleTripleTrailers: "Haul Double/Triple Trailers",
  fein: "FEIN",
  revenue: "Revenue",
  miles: "Miles",
  operatedTwoYears: "Operated ≥ 2y",
  personalUseOfTrailer: "Trailer Personal Use",
  personalUseOfVehicle: "Vehicle Personal Use",
  insuredWithProgressiveCommercialAuto: "Insured with Progressive (CA)",
  insuredName: "Insured",
  "dot.dotNumber": "USDOT",
  hasLossRuns: "Loss Runs",
  insuredIssuedWithInLastSixtyDays: "Issued ≤ 60d",
  primaryContactTitle: "Primary Contact Title",
  ownershipChangeLast12Months: "Ownership Change (12mo)",
  dotSafetyRating: "DOT Safety Rating",
  priorCancelled: "Prior Cancelled",
  cancellations12mo: "Cancellations (12mo)",
  uiiaIntermodal: "UIIA Intermodal",
  filingsAlFederal: "Filings Al Federal",
  filingsAlState: "Filings Al State",
  dumpLoadsTarped: "Dump Loads Tarped",
  oversizedShipments: "Oversized Shipments",
  brokerageAuthority: "Brokerage Authority",
  ownerOperators: "Owner Operators",
  interlineAgreement: "Interline Agreement",
  interchangeAgreement: "Interchange Agreement",
  intermodalAgreement: "Intermodal Agreement",
  leaseVehiclesTrailers: "Lease Vehicles Trailers",
  travelMexico: "Travel Mexico",
  travelCanada: "Travel Canada",
  teamDrivers: "Team Drivers",
  guestPassengers: "Guest Passengers",
  subsidiary: "Subsidiary",
  tripLeasingUnderAuthority: "Trip Leasing Under Authority",
  corgiTravelDistance: "Corgi Travel Distance",
  corgiPassengerOperations: "Passenger Operations",
  corgiFreightForwarder: "Freight Forwarder",
  corgiTransitMixerOperations: "Transit Mixer Operations",
  corgiDeliveryCourierOperations: "Delivery / Courier Operations",
  corgiArmoredVehicles: "Armored Vehicles",
  corgiMostRunsUnder100Miles: "Most Runs Under 100 Miles",
  wayOfCompensation: "Way of Compensation",
  companyRulesProcedures: "Company Rules/Procedures",
  dailyVehicleInspections: "Daily Vehicle Inspections",
  equipmentFamiliarization: "Equipment Familiarization",
  routeFamiliarization: "Route Familiarization",
  emergencyProcedures: "Emergency Procedures",
  accidentReportingProcedures: "Accident Reporting Procedures",
  writtenDriverSafetyManual: "Written Driver Safety Manual",
  mvrsCheckedAnnually: "MVRs Checked Annually",
  driverHomeDaysPerWeek: "Driver Home Days/Week",
  driversDotCompliant: "DOT Compliant",
  minimumAgeOfDrivers: "Minimum Age of Drivers",
  violationsPastSixMonths: "Violations (6mo)",
  zeroTolerancePolicyForMajorViolations: "Zero Tolerance Policy",
  safetyRewardsProgram: "Safety Rewards Program",
  disciplinaryActionForDrivers: "Disciplinary Action",
  howOftenAreMandatorySafetyMeetingsHeld: "Safety Meetings Frequency",
  maintainAccidentRegister: "Maintain Accident Register",
  writtenMaintenanceProgram: "Written Maintenance Program",
  insuredDoTheirOwnMaintenance: "Insured Does Own Maintenance",
  cdlExperience: "CDL Experience",
  vehicleFilesDotCompliant: "Vehicle Files DOT Compliant",
  freeBoardSixInches: "Freeboard ≥ 6",
  roadsideConstruction: "Roadside / Construction",
  "operatingStart.month": "Start Month",
  "operatingStart.year": "Start Year",
  entityType: "Entity",
  business_description: "Description",
  hasIneligibleOps: "Ineligible Ops",
  noneOfMentioned: "None of the Above",
  telemetryOption: "Data Sharing Option",
  telemetryProvider: "Provider Name",
  hasEld: "Has ELD?",
  eldProviderKey: "ELD Provider (code)",
  eldProviderOtherText: "ELD Provider (other)",
  hasDashCam: "Has Dash Camera?",
  dashCamProviderText: "Dash Cam Provider",
  personalUse: "Personal Use",
  "vehicles:length": "Vehicles",
  "trailers:length": "Trailers",
  auto_liability: "Auto Liability",
  motor_truck_cargo: "Motor Truck Cargo",
  automobile_physical_damage: "APD",
  truckers_general_liability: "TGL",
  non_trucking_liability: "NTL",
  personal_injury_protection: "PIP",
  uninsured_motorists_property_damage: "UMPD",
  trailer_interchange: "Trailer Interchange",
  currentBILimitChecked: "Current BI Limit (set?)",
  hiredAutoLiability: "Hired Auto Liability",
  rentalReimbursement: "Rental Reimbursement",
  roadsideAssistance: "Roadside Assistance",
  blanketAdditionalInsured: "Blanket AI",
  compCollisionDeductibleChecked: "Comp/Collision Deductible (set?)",
  nonOwnedTrailerCoverage: "Non-Owned Trailer Coverage",
  corgiMedicalPayments: "Corgi Medical Payments",
  corgiTriaCoverage: "Corgi TRIA Coverage",
  corgiUmUimCoverage: "Corgi UM/UIM Coverage",
  corgiTowTrucks: "Corgi Tow Trucks",
};

const STEP_FIELDS = {
  1: [
    "carrier",
    "policyDate",
    "hazmatPlacard",
    "haulDoubleTripleTrailers",
    "operatedTwoYears",
    "personalUseOfTrailer",
    "personalUseOfVehicle",
    "insuredWithProgressiveCommercialAuto",
  ],
  2: [
    "insuredName",
    "fein",
    "revenue",
    "miles",
    "dot.dotNumber",
    "hasLossRuns",
    "insuredIssuedWithInLastSixtyDays",
    "primaryContactTitle",
    "ownershipChangeLast12Months",
    "dotSafetyRating",
  ],
  3: [
    "business_description",
    "operatingStart.month",
    "operatingStart.year",
    "entityType",
    "priorCancelled",
    "cancellations12mo",
    "uiiaIntermodal",
    "dumpLoadsTarped",
    "freeBoardSixInches",
    "roadsideConstruction",
    "hasIneligibleOps",
    "noneOfMentioned",
    "oversizedShipments",
    "brokerageAuthority",
    "ownerOperators",
    "interlineAgreement",
    "interchangeAgreement",
    "intermodalAgreement",
    "leaseVehiclesTrailers",
    "travelMexico",
    "travelCanada",
    "teamDrivers",
    "guestPassengers",
    "subsidiary",
    "tripLeasingUnderAuthority",
    "corgiTravelDistance",
    "corgiPassengerOperations",
    "corgiFreightForwarder",
    "corgiTransitMixerOperations",
    "corgiDeliveryCourierOperations",
    "corgiArmoredVehicles",
    "corgiMostRunsUnder100Miles",
  ],
  4: [
    "telemetryOption",
    "telemetryProvider",
  ],
  5: [
    { path: "drivers:length", label: "Drivers" },
    "wayOfCompensation",
    "companyRulesProcedures",
    "dailyVehicleInspections",
    "equipmentFamiliarization",
    "routeFamiliarization",
    "emergencyProcedures",
    "accidentReportingProcedures",
    "writtenDriverSafetyManual",
    "mvrsCheckedAnnually",
    "driverHomeDaysPerWeek",
    "driversDotCompliant",
    "minimumAgeOfDrivers",
    "violationsPastSixMonths",
    "zeroTolerancePolicyForMajorViolations",
    "safetyRewardsProgram",
    "disciplinaryActionForDrivers",
    "howOftenAreMandatorySafetyMeetingsHeld",
    "maintainAccidentRegister",
    "writtenMaintenanceProgram",
    "insuredDoTheirOwnMaintenance",
    "cdlExperience",
  ],
  6: [
    "personalUse", { path: "vehicles:length", label: "Vehicles" },
    "vehicleFilesDotCompliant"
  ],
  7: [{ path: "trailers:length", label: "Trailers" }],
  8: [
    "auto_liability",
    "motor_truck_cargo",
    "automobile_physical_damage",
    "truckers_general_liability",
    "non_trucking_liability",
    "personal_injury_protection",
    "uninsured_motorists_property_damage",
    "trailer_interchange",
    "currentBILimitChecked",
    "hiredAutoLiability",
    "rentalReimbursement",
    "roadsideAssistance",
    "blanketAdditionalInsured",
    "compCollisionDeductibleChecked",
    "nonOwnedTrailerCoverage",
    "corgiMedicalPayments",
    "corgiTriaCoverage",
    "corgiUmUimCoverage",
    "corgiTowTrucks",
  ],
};

function get(obj, path) {
  if (!path) return undefined;
  if (path.endsWith(":length")) {
    const real = path.replace(/:length$/, "");
    const val = get(obj, real);
    return Array.isArray(val) ? val.length : undefined;
  }
  return path.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

function isDateLike(val) {
  return val instanceof Date || (typeof val === "string" && !Number.isNaN(Date.parse(val)));
}

function normalizeDate(val) {
  if (val instanceof Date) return val;
  if (typeof val === "string") {
    const d = new Date(val);
    return Number.isNaN(+d) ? undefined : d;
  }
  return undefined;
}

function booleanLike(val) {
  if (typeof val === "boolean") return { match: true, value: val };
  if (val == null) return { match: false, value: null };

  if (typeof val === "string") {
    const s = val.trim().toLowerCase();
    if (["yes", "y", "true", "1"].includes(s)) return { match: true, value: true };
    if (["no", "n", "false", "0"].includes(s)) return { match: true, value: false };
    if (["na", "n/a", "none", "unselected"].includes(s)) return { match: true, value: null };
  }

  return { match: false, value: null };
}

function renderValue(value, path) {
  if (path === "fein") {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 9);
    const formatted = digits.length > 2
      ? `${digits.slice(0, 2)}-${digits.slice(2)}`
      : digits;
    return <Pill>{formatted || "—"}</Pill>;
  }

  if (path === "corgiTravelDistance") {
    const labels = {
      "0_50": "0-50",
      "51_200": "51-200",
      "200_plus": "200+",
      nationwide_longhaul: "Nationwide / Longhaul",
    };
    return <Pill>{labels[value] || value || "—"}</Pill>;
  }

  const bl = booleanLike(value);
  if (bl.match) {
    return <YesNo v={bl.value == null ? undefined : bl.value} />;
  }

  if (isDateLike(value)) {
    const d = normalizeDate(value);
    return <Pill>{d ? fmtDate(d) : "—"}</Pill>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <Pill>—</Pill>;
    return (
      <>
        {value.map((it, i) => (
          <Pill key={i}>{String(it)}</Pill>
        ))}
      </>
    );
  }

  if (typeof value === "object" && value !== null) {
    if (value.projected !== undefined && value.current !== undefined) {
      const labels = getPeriodLabels({
        withMilesSuffix: path?.toLowerCase().includes("miles"),
      });

      return (
        <>
          <Pill>{labels.projected}: {value.projected}</Pill>
          <Pill>{labels.current}: {value.current}</Pill>
          <Pill>{labels.firstPrior}: {value.firstPrior}</Pill>
          <Pill>{labels.secondPrior}: {value.secondPrior}</Pill>
        </>
      );
    }

    return <Pill>—</Pill>;
  }

  if (value === "" || value == null) return <Pill>—</Pill>;
  return <Pill>{String(value)}</Pill>;
}

function FieldRow({ label, children }) {
  return (
    <div className="flex gap-2 items-center">
      <p className="shrink-0">{label}</p>
      <div className="flex gap-2 flex-wrap">{children}</div>
    </div>
  );
}

const MONTHS_MAP = {
  "01": "January",
  "02": "February",
  "03": "March",
  "04": "April",
  "05": "May",
  "06": "June",
  "07": "July",
  "08": "August",
  "09": "September",
  "10": "October",
  "11": "November",
  "12": "December",
};

export function Highlights({ idx, data }) {
  const raw = STEP_FIELDS[idx] || [];
  if (raw.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {raw.map((entry, i) => {
        const path = typeof entry === "string" ? entry : entry.path;
        const label =
          (typeof entry !== "string" && entry.label) ||
          LABELS[path] ||
          path.split(".").slice(-1)[0];

        let val = get(data, path);

        if (path === "business_description" && Array.isArray(val)) {
          return (
            <FieldRow key={i} label={LABELS[path] || "Description"}>
              {val.length ? val.map((v, j) => <Pill key={j}>{String(v)}</Pill>) : <Pill>—</Pill>}
            </FieldRow>
          );
        }

        if (path === "carrier" && Array.isArray(val)) {
          return (
            <FieldRow key={i} label={LABELS[path] || "Carrier"}>
              {val.length ? val.map((c, j) => <Pill key={j}>{String(c)}</Pill>) : <Pill>—</Pill>}
            </FieldRow>
          );
        }

        if (path === "operatingStart.month") {
          const monthLabel = MONTHS_MAP[val] || val || "—";
          return (
            <FieldRow key={i} label={label}>
              <Pill>{monthLabel}</Pill>
            </FieldRow>
          );
        }

        if (path === "operatingStart.year") {
          return (
            <FieldRow key={i} label={label}>
              <Pill>{val}</Pill>
            </FieldRow>
          );
        }

        return (
          <FieldRow key={i} label={label}>
            {renderValue(val, path)}
          </FieldRow>
        );
      })}
    </div>
  );
}

/* ------------------- per-step icon ------------------- */
const stepIcon = (idx) => {
  const map = {
    1: CalendarDays,
    2: User,
    3: Layers,
    4: Info,
    5: PackageSearch,
    6: Truck,
    7: Car,
    8: ShieldCheck,
    9: FileSpreadsheet,
  };
  return map[idx] ?? Info;
};

/* ------------------- main component ------------------- */
export function ReviewAndSubmit() {
  const { getValues, formState } = useFormContext();
  const data = getValues();

  const carriers = React.useMemo(() => {
    const arr = data?.step1?.carrier;
    if (Array.isArray(arr)) return arr.map((c) => String(c));
    if (arr) return [String(arr)];
    return [];
  }, [data?.step1?.carrier]);

  const hasRequiredDocuments = getRequiredDocumentTypes(data).length > 0;

  const hasTelemetryCarrier = carriers.some(
    (c) => c === "Cover Whale" || c === "Nirvana"
  );

  const steps = React.useMemo(() => {
    const base = [
      { key: "step1", title: "Initial Information", idx: 1 },
      { key: "step2", title: "Insured", idx: 2 },
      { key: "step3", title: "Operations", idx: 3 },
      { key: "step4", title: "Data Sharing", idx: 4 },
      { key: "step5", title: "Drivers", idx: 5 },
      { key: "step6", title: "Vehicles", idx: 6 },
      { key: "step7", title: "Trailers", idx: 7 },
      { key: "step8", title: "Coverages", idx: 8 },
      { key: "step9", title: "Losses", idx: 9 },
      { key: "step10", title: "Documents", idx: 10 },
    ];
    return base
      .filter((s) => s.key !== "step10" || hasRequiredDocuments)
      .filter((s) => s.key !== "step4" || hasTelemetryCarrier);
  }, [hasRequiredDocuments, hasTelemetryCarrier]);

  const errorKeys = React.useMemo(() => Object.keys(formState.errors ?? {}), [formState.errors]);

  const totalDrivers = data?.step5?.drivers?.length ?? 0;
  const totalVehicles = data?.step6?.vehicles?.length ?? 0;
  const totalTrailers = data?.step7?.trailers?.length ?? 0;

  const errorCount = errorKeys.length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-6">
      {/* left rail: stepper */}
      <aside className="lg:sticky lg:top-4 self-start">
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Submission Checklist</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ScrollArea className="h-[420px] pr-2">
              <div className="space-y-2">
                {steps.map((s) => {
                  const hasErrors = errorKeys.some((ek) => ek.startsWith(String(s.key)));
                  const Icon = stepIcon(s.idx);
                  return (
                    <button
                      key={s.idx}
                      type="button"
                      onClick={() => {
                        const el = document.getElementById(`section-${s.idx}`);
                        el?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      className={`w-full text-left flex items-center gap-3 rounded-lg border px-3 py-2 transition ${
                        hasErrors
                          ? "border-rose-200 bg-rose-50/60 hover:bg-rose-50"
                          : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${hasErrors ? "text-rose-600" : "text-emerald-600"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">Step {s.idx} — {s.title}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {hasErrors ? "Needs attention" : "Looks good"}
                        </div>
                      </div>
                      {hasErrors ? (
                        <XCircle className="w-4 h-4 text-rose-600" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      )}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </aside>

      {/* right: content */}
      <div className="space-y-6">
        {/* sticky summary bar */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  {errorCount > 0 ? (
                    <Badge className="bg-rose-600 hover:bg-rose-600">{errorCount} issue(s)</Badge>
                  ) : (
                    <Badge className="bg-emerald-600 hover:bg-emerald-600">Ready</Badge>
                  )}
                  <div className="text-sm text-muted-foreground">Review details below.</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Stat icon={CalendarDays} label="Policy Date" value={fmtDate(data?.step1?.policyDate)} />
                  <Stat icon={User} label="Drivers" value={totalDrivers} />
                  <Stat icon={Truck} label="Vehicles" value={totalVehicles} />
                  <Stat icon={Car} label="Trailers" value={totalTrailers} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* sections */}
        {steps.map((s) => {
          const sectionData = data?.[s.key];
          const hasErrors = errorKeys.some((ek) => ek.startsWith(String(s.key)));
          const Icon = stepIcon(s.idx);

          return (
            <section key={s.idx} id={`section-${s.idx}`} className="scroll-mt-24">
              <Card className={`border ${hasErrors ? "border-rose-200" : "border-slate-200"} bg-white/90 shadow-sm`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon className={`w-5 h-5 ${hasErrors ? "text-rose-600" : "text-emerald-600"}`} />
                      <CardTitle className="text-base font-semibold truncate">Step {s.idx} — {s.title}</CardTitle>
                      {hasErrors ? (
                        <Badge className="bg-rose-600 hover:bg-rose-600 gap-1"><AlertTriangle className="w-3 h-3" /> Issues</Badge>
                      ) : (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600 gap-1"><CheckCircle2 className="w-3 h-3" /> Looks good</Badge>
                      )}
                    </div>
                  </div>
                  <div className="pt-2">{/* quick highlights */}
                    <Highlights idx={s.idx} data={sectionData} />
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  {isEmpty(sectionData) ? (
                    <div className="text-sm text-muted-foreground">No data provided.</div>
                  ) : (
                    <div className="space-y-2">
                      {/* <SectionObjectBlocks data={sectionData} /> */}
                      {/* Arrays preview as compact list of cards */}
                      <ArrayBlocks data={sectionData} step={s.idx} />
                    </div>
                  )}
                </CardContent>
              </Card>
            </section>
          );
        })}

        {/* final note */}
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-muted-foreground">
            Ensure every section shows <span className="text-emerald-700 font-medium">Looks good</span> before submitting.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------- Arrays renderer ------------------- */
const ArrayBlocks = ({ data, step }) => {
  if (!data || typeof data !== "object") return null;
  const arrays = Object.entries(data).filter(([_, v]) => Array.isArray(v));
  if (arrays.length === 0) return null;

  const filtered = arrays.filter(([k, _]) => k !== "carriers" && (step !== 1 && k !== 'carrier') );
  return (
    <div className="space-y-4">
      {filtered.map(([k, arr]) => (
        <Accordion type="single" collapsible key={k} className="w-full">
          <AccordionItem value={k} className="!border-b-2 rounded-lg border bg-white/70">
            <AccordionTrigger className="px-3 py-2 rounded-lg hover:no-underline after:hidden data-[state=open]:bg-slate-50">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{labelize(k)}</span>
                <Badge variant="secondary" className="ml-1">{arr?.length ?? 0}</Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3">
                {(arr || []).map((row, i) => (
                  <Card key={i} className="border-muted/60 bg-slate-50 m-3">
                    <CardContent className="p-4">
                      {typeof row === "object" && row ? <KVFlat data={row} /> : (
                        <div className="text-sm">{String(row)}</div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ))}
    </div>
  );
};
